<script lang="ts">
	import Icon from '$lib/components/icons/Icon.svelte';

	import { createSelect } from '@melt-ui/svelte';
	import { fade } from 'svelte/transition';

	type T = $$Generic;
	interface Props {
		options: { value: T; label: string }[];
		ariaLabel: string;
		disabled?: boolean;
		defaultValue?: string;
		dropDownSameWidth?: boolean;
		label?: string;
		onSelectedChange?: (value: T | undefined) => void;
		onOpenChange?: () => void;
	}

	const {
		options,
		ariaLabel,
		disabled = false,
		defaultValue = undefined,
		dropDownSameWidth = false,
		label = undefined,
		onSelectedChange,
		onOpenChange,
	}: Props = $props();

	const {
		elements: { trigger, menu, option, label: meltLabel },
		states: { selectedLabel, open, selected },
		helpers: { isSelected, isHighlighted },
	} = createSelect<T>({
		portal: null,
		forceVisible: true,
		positioning: {
			placement: 'bottom',
			fitViewport: true,
			sameWidth: dropDownSameWidth,
		},
		defaultSelected: options.find(o => o.value === defaultValue),
		onSelectedChange({ next, curr }) {
			if (next?.value !== curr?.value) {
				onSelectedChange?.(next?.value);
			}
			return next;
		},
		onOpenChange({ next }) {
			const isOpen = next;
			if (isOpen) {
				onOpenChange?.();
			}
			return next;
		},
		required: true,
	});

	$effect.pre(() => {
		$selected = options.find(option => option.value === defaultValue);
	});
</script>

<div class="flex flex-col gap-1">
	{#if label}
		<!-- eslint-disable-next-line svelte/require-store-reactive-access -->
		<label class="text-sm" {...$meltLabel} use:meltLabel for={$trigger.id}>
			{label}
		</label>
	{/if}
	<!-- eslint-disable svelte/require-store-reactive-access -->
	<button
		class={['flex justify-between items-center h-11 p-2 text-body text-md text-coloredneutral bg-neutral-weakest hover:bg-neutral-weak rd-2 b-0.5 b-solid border-coloredneutral-weakest cursor-pointer disabled:cursor-default disabled:bg-neutral-soft disabled:text-coloredneutral-weak', $open && 'border-focus shadow-focus']}
		{...$trigger}
		use:trigger
		aria-label={ariaLabel}
		{disabled}
	>
		<span class="truncate">
			{$selectedLabel}
		</span>
		<Icon icon="expand-more" class="text-5" tag="span" />
	</button>
	{#if $open}
		<!-- eslint-disable-next-line svelte/require-store-reactive-access -->
		<div
			class="z-hover flex flex-col overflow-y-auto bg-neutral-weakest shadow-smooth-bottom-2 rd-1 p-1"
			{...$menu}
			use:menu
			transition:fade={{ duration: 150 }}
		>
			<!-- eslint-disable-next-line svelte/require-each-key -->
			{#each options as item}
				<!-- eslint-disable-next-line svelte/require-store-reactive-access -->
				<div
					class="relative cursor-pointer rd-1 py-2 px-1 text-body text-md text-coloredneutral"
					{...$option(item)}
					class:bg-primary-weakest={$isSelected(item.value)}
					class:bg-neutral-soft={$isHighlighted(item.value)}
					use:option
				>
					{item.label}
				</div>
			{/each}
		</div>
	{/if}
</div>
